import { useSubscription } from "@apollo/client";
import { useEffect, useState } from "react";
import { DASHBOARD_METRICS_QUERY } from "@/graphql/appointments";
import { DASHBOARD_METRICS_SUBSCRIPTION } from "@/graphql/subscriptions";
import { getWebSocketConnectionState } from "@/lib/apollo/apollo-client";

interface UseDashboardMetricsSubscriptionProps {
  salonId: string;
  enabled?: boolean;
}

interface UseDashboardMetricsSubscriptionReturn {
  isConnected: boolean;
  error?: Error;
  connectionState: "disconnected" | "connecting" | "connected" | "error";
}

/**
 * Hook to subscribe to dashboard metrics updates
 * Automatically updates the Apollo Client cache when metrics change
 *
 * @param salonId - The salon ID to subscribe to
 * @param enabled - Whether the subscription is enabled (default: true)
 * @returns Connection status and error information
 */
export const useDashboardMetricsSubscription = ({
  salonId,
  enabled = true,
}: UseDashboardMetricsSubscriptionProps): UseDashboardMetricsSubscriptionReturn => {
  const [connectionState, setConnectionState] = useState<
    "disconnected" | "connecting" | "connected" | "error"
  >("disconnected");

  const { error } = useSubscription(DASHBOARD_METRICS_SUBSCRIPTION, {
    variables: { salonId: String(salonId) }, // Ensure salonId is a clean string
    skip: !enabled || !salonId,
    onData: ({ data: subscriptionData, client }) => {
      if (!subscriptionData?.data?.dashboardMetricsUpdates) return;

      setConnectionState("connected");

      const { dashboardMetrics } = subscriptionData.data.dashboardMetricsUpdates;

      if (!dashboardMetrics) {
        console.warn("[Dashboard Metrics] No dashboard metrics in subscription data");
        return;
      }

      // Update the Apollo Client cache for the dashboardMetrics query
      // Use writeQuery to update the parameterized query cache
      try {
        client.cache.writeQuery({
          query: DASHBOARD_METRICS_QUERY,
          variables: { salonId: String(salonId) },
          data: {
            dashboardMetrics,
          },
        });
      } catch (error) {
        console.error("[Dashboard Metrics] Failed to update cache:", error, {
          dashboardMetrics,
          salonId,
        });
      }
    },
    onError: (subscriptionError) => {
      console.error("[Dashboard Metrics] Subscription error:", subscriptionError);
      setConnectionState("error");
    },
    onComplete: () => {
      setConnectionState("disconnected");
    },
  });

  // Monitor WebSocket connection state
  useEffect(() => {
    if (!enabled || !salonId) {
      setConnectionState("disconnected");
      return;
    }

    const checkConnection = () => {
      const wsState = getWebSocketConnectionState();

      if (wsState === "connected" && !error) {
        setConnectionState("connected");
      } else if (wsState === "connecting") {
        setConnectionState("connecting");
      } else if (wsState === "error" || error) {
        setConnectionState("error");
        console.error(`[Dashboard Metrics] WebSocket error for salon ${salonId}:`, error);
      } else {
        setConnectionState("disconnected");
      }
    };

    checkConnection();

    const handleConnected = () => checkConnection();
    const handleDisconnected = () => setConnectionState("disconnected");

    window.addEventListener("websocket-connected", handleConnected);
    window.addEventListener("websocket-disconnected", handleDisconnected);

    return () => {
      window.removeEventListener("websocket-connected", handleConnected);
      window.removeEventListener("websocket-disconnected", handleDisconnected);
    };
  }, [error, enabled, salonId]);

  const isConnected = connectionState === "connected" && !error;

  return {
    isConnected,
    error,
    connectionState,
  };
};
